/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useU2000GridStateHandlers } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU2000SearchStateHandlers } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import type { U2000GridStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import type { U2000SearchStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import { useU2000LUStateHandlers } from '../u2000Core/reducer/formReducer/U2000LUHandlers';
import type { U2000LUState } from '../u2000Core/reducer/formReducer/U2000LUState';
import type { U3157InitLU, U3157ImportModelSearchRow } from './U3157Service';

export type InitLU = U2000LUState['personalization'] &
  U3157InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3157ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3157ImportModelSearchRow>;
  initLU: U3157InitLU;
  setInitLU: React.Dispatch<React.SetStateAction<U3157InitLU>>;
}

interface U3157ContextProviderProps {
  initLU: U3157InitLU;
  setInitLU: React.Dispatch<React.SetStateAction<U3157InitLU>>;
  children: ReactNode;
}

const U3157Context = React.createContext<U3157ContextType | undefined>(undefined);

export const U3157ContextProvider: React.FC<U3157ContextProviderProps> = ({ initLU, setInitLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: 'u3157',
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3157ImportModelSearchRow>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });

  const contextValue = useMemo<U3157ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      initLU,
      setInitLU,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers, initLU, setInitLU],
  );

  return <U3157Context.Provider value={contextValue}>{children}</U3157Context.Provider>;
};

export function useU3157Context(): U3157ContextType {
  const context = useContext(U3157Context);
  if (!context) {
    throw new Error('useU3157Context must be used within a U3157ContextProvider');
  }
  return context;
}
